/**
 * Life4Earth V2 — Impact Signature API Adapter
 *
 * Endpoints:
 *   GET /api/users/{userGuid}/impact-summary    — Aggregated tier levels + scores
 *   GET /api/users/{userGuid}/impact-events     — Recent impact event history
 *   GET /api/users/{userGuid}/active-roles      — Cross-project active role assignments
 *   GET /api/users/{userGuid}/completed-roles   — Cross-project completed role assignments
 *
 * Consumed by:
 *   - useApiImpactSignature hook → DimensionRadar, ImpactSignatureMini
 *   - useApiUserRoles hook → ActiveRolesPanel
 *   - MyImpactPage (Growth tab)
 *   - UserProfilePage (Impact tab)
 */

import { apiFetch } from "./client";

// ════════════════════════════════════════════════════════════════
// Response Types
// ════════════════════════════════════════════════════════════════

export type TierLevel = "none" | "bronze" | "silver" | "gold" | "platinum";

export interface ImpactSummaryResponse {
  /** Computed tier per dimension: { mind: "gold", body: "silver", ... } */
  tiers: Record<string, TierLevel>;
  /** Raw cumulative score per dimension. */
  scores: Record<string, number>;
  /** Total number of impact events for this user. */
  totalEvents: number;
}

export interface ImpactEventResponse {
  eventGuid: string;
  userGuid: string;
  dimensionWeights: string; // JSONB string
  magnitude: number;
  sourceType: string;
  sourceGuid: string;
  projectGuid: string | null;
  communityGuid: string | null;
  isCrossCommunity: boolean;
  heartMultiplier: number;
  createdAt: string;
}

// ════════════════════════════════════════════════════════════════
// API Functions
// ════════════════════════════════════════════════════════════════

/**
 * Fetch aggregated impact summary (tier levels + scores) for a user.
 * This is the primary data source for DimensionRadar and ImpactSignatureMini.
 */
export async function fetchImpactSummary(
  userGuid: string
): Promise<ImpactSummaryResponse> {
  return apiFetch<ImpactSummaryResponse>(
    `/api/users/${userGuid}/impact-summary`
  );
}

/**
 * Fetch recent impact events for a user (history display).
 */
export async function fetchImpactEvents(
  userGuid: string,
  limit?: number
): Promise<ImpactEventResponse[]> {
  const params = limit ? `?limit=${limit}` : "";
  return apiFetch<ImpactEventResponse[]>(
    `/api/users/${userGuid}/impact-events${params}`
  );
}

// ════════════════════════════════════════════════════════════════
// Admin Grant — System admin awards impact credit
// ════════════════════════════════════════════════════════════════

export interface AdminGrantRequest {
  /** Recipient user GUID. */
  userGuid: string;
  /** Dimension to grant credit for (mind, body, heart, wings, roots, voice). */
  dimension: string;
  /** Credit magnitude (score points to add). */
  magnitude: number;
  /** Reason / justification for the grant. */
  reason: string;
  /** Optional project association. */
  projectGuid?: string | null;
  /** Optional community association. */
  communityGuid?: string | null;
}

/**
 * System admin: Grant impact credit to a user.
 * Creates an impact_event with source_type = "admin_grant".
 */
export async function adminGrantImpactCredit(
  req: AdminGrantRequest
): Promise<ImpactEventResponse> {
  return apiFetch<ImpactEventResponse>(
    `/api/admin/impact-events/grant`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

// ════════════════════════════════════════════════════════════════
// User Role Assignments — cross-project view
// ════════════════════════════════════════════════════════════════

export interface UserRoleAssignmentResponse {
  assignmentGuid: string;
  roleSlotGuid: string;
  userGuid: string;
  status: string;
  assignedAt: string;
  completedAt: string | null;
  impactAccrued: boolean;
  notes: string | null;
  instructions: string | null;
  evidenceUrls: string[];
  evidenceNotes: string | null;
  // Slot context (Tier 2)
  roleLabel: string;
  roleCategory: string;
  roleDescription: string | null;
  timeCommitment: string | null;
  verificationLevel: string;
  skillsRequired: string[];
  // Duration context
  durationType: string;
  expectedStartDate: string | null;
  expectedEndDate: string | null;
  // Project context
  projectGuid: string;
  projectName: string;
  // Verification context (for completed roles)
  completedVerifiedBy: string | null;
  completedVerifiedAt: string | null;
  // Invitation context
  invitedAt: string | null;
  invitationRespondedAt: string | null;
  // Resignation context
  resignedAt: string | null;
  resignationReason: string | null;
  /**
   * DEV-1091. Optional fields populated by Mini-Project rows so the
   * Engagements detail modal can render the full role spec without
   * a second fetch. Both are null/undefined for Full Project rows.
   */
  responsibilities?: string | null;
  invitationMessage?: string | null;
  /**
   * DEV-1050 follow-up. Discriminates the source of the engagement.
   * "role" = operational role-slot assignment; "governance" = direct
   * permission_grants row (Admin / Observer / custom user-mode bundle).
   * "mini-project" (DEV-1088) — the assignment row is a
   * mini_project_role_assignments row (state-machine row in the
   * Mini-Project flow).
   * Defaults to "role" on the wire when omitted, for backward compat
   * with older server payloads.
   */
  engagementType?: "role" | "governance" | "mini-project";
  /**
   * DEV-1164. Role definition GUID this slot was instantiated from.
   * Lets the Engagements right pane resolve the attached rhythm via
   * GET /api/rolerhythms/by-definition/{guid}. Null for governance
   * grants (no slot) and any slot whose source_definition_guid is
   * unset.
   */
  sourceDefinitionGuid?: string | null;
}

/**
 * Fetch all active role assignments for a user across all projects.
 * "Active" includes: active, pending_verification, pending_escalated_review, pending_invitation.
 */
export async function fetchUserActiveRoles(
  userGuid: string
): Promise<UserRoleAssignmentResponse[]> {
  return apiFetch<UserRoleAssignmentResponse[]>(
    `/api/users/${userGuid}/active-roles`
  );
}

/**
 * Fetch completed role assignments for a user across all projects.
 */
export async function fetchUserCompletedRoles(
  userGuid: string,
  limit?: number
): Promise<UserRoleAssignmentResponse[]> {
  const params = limit ? `?limit=${limit}` : "";
  return apiFetch<UserRoleAssignmentResponse[]>(
    `/api/users/${userGuid}/completed-roles${params}`
  );
}

/**
 * Overall signature state (DEV-1446 §7.6-as-amended): the signature "fills"
 * with a medal only when ALL six dimensions carry it (DEV-1301) — overall =
 * the minimum tier; `lit` = dimensions with any medal. ONE helper consumed
 * by both the rail card and the full page so the status label can never
 * disagree between the gateway and the destination.
 */
export function overallSignature(
  tiers: Record<string, TierLevel> | null | undefined,
): { lit: number; overall: Exclude<TierLevel, "none"> | null } {
  const DIMS = ["mind", "voice", "heart", "body", "wings", "roots"];
  const rank: Record<TierLevel, number> = {
    none: 0,
    bronze: 1,
    silver: 2,
    gold: 3,
    platinum: 4,
  };
  let lit = 0;
  let min: TierLevel | null = null;
  for (const d of DIMS) {
    const t = (tiers?.[d] ?? "none") as TierLevel;
    if (t !== "none") lit += 1;
    if (min === null || rank[t] < rank[min]) min = t;
  }
  return { lit, overall: min === "none" || min === null ? null : (min as Exclude<TierLevel, "none">) };
}
